/**
 * 전장 범위 효과 타겟 선택 — Phaser-free 순수 함수.
 *
 * BattlefieldRunner의 applyExplosion / applyChain 내 필터·정렬을 추출.
 * 피해 적용·FX는 BattlefieldRunner에 남긴다.
 */

/** 위치 + 식별(참조 비교) 가능한 최소 구조. */
export interface AreaTarget {
  readonly x: number;
  readonly y: number;
  readonly active: boolean;
}

function distSq(ax: number, ay: number, bx: number, by: number): number {
  const dx = ax - bx;
  const dy = ay - by;
  return dx * dx + dy * dy;
}

/**
 * 폭발 범위 내 대상 선택 — exclude를 제외하고 radius 이내 활성 객체를 (launcherX, launcherY) 거리순 정렬.
 * 멜로디 챕터에서 가까운 블록부터 곡이 흐르도록 launcher 거리순.
 */
export function selectExplosionTargets<T extends AreaTarget>(
  items: Iterable<T>,
  epicenterX: number,
  epicenterY: number,
  radius: number,
  exclude: T,
  launcherX: number,
  launcherY: number,
): T[] {
  const radiusSq = radius * radius;
  const targets: { item: T; launcherDistSq: number }[] = [];
  for (const b of items) {
    if (b === exclude || !b.active) continue;
    if (distSq(epicenterX, epicenterY, b.x, b.y) > radiusSq) continue;
    targets.push({ item: b, launcherDistSq: distSq(launcherX, launcherY, b.x, b.y) });
  }
  targets.sort((a, c) => a.launcherDistSq - c.launcherDistSq);
  return targets.map((t) => t.item);
}

/**
 * 연쇄(chain) 대상 선택 — source를 제외하고 (sourceX, sourceY) 거리순으로 count개까지.
 */
export function selectChainTargets<T extends AreaTarget>(
  items: Iterable<T>,
  source: T,
  sourceX: number,
  sourceY: number,
  count: number,
): T[] {
  const targets: { item: T; distSq: number }[] = [];
  for (const b of items) {
    if (b === source || !b.active) continue;
    targets.push({ item: b, distSq: distSq(sourceX, sourceY, b.x, b.y) });
  }
  targets.sort((a, c) => a.distSq - c.distSq);
  return targets.slice(0, count).map((t) => t.item);
}
